import { FileText, X } from "lucide-react";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import {
  capitalize,
  formatDate,
  formatPaymentMethod,
} from "../../../utils/helpers";

const Chip = ({ label, onRemove }) => (
  <span className="flex items-center gap-2 px-3 py-1 text-xs rounded-full bg-[#F7BFAD]/40 text-[#f0653a]">
    {label}
    <button
      onClick={onRemove}
      className="p-1 hover:bg-[#F7BFAD]/70 rounded-full transition"
    >
      <X className="size-3" />
    </button>
  </span>
);

const ReportFilters = ({
  /* state */
  filters,
  appliedFilters,
  setFilters,
  onApply,
  onClearAll,
  onRemoveFilter,

  /* data */
  staffList = [],

  /* visibility controls */
  showReportType = true,
  showDateRange = true,
  showPaymentMode = false,
  showStaff = false,

  /* export */
  onExportPDF,
  onExportExcel,
}) => {
  const activeFilters = Object.entries(appliedFilters)
    .filter(([key, value]) => value && key !== "reportType")
    .map(([key, value]) => {
      const labels = {
        fromDate: `From: ${formatDate(value)}`,
        toDate: `To: ${formatDate(value)}`,
        paymentMode: `Payment: ${formatPaymentMethod(value)}`,
        staff: `Staff: ${value}`,
      };

      return {
        key,
        label: labels[key] || `${capitalize(key)}: ${value}`,
      };
    });

  const hasUnappliedChanges = Object.keys(filters).some(
    (key) => filters[key] !== appliedFilters[key],
  );

  const hasAppliedFilters = activeFilters.length > 0;

  return (
    <div
      className="bg-white border border-[#F7BFAD] rounded-2xl space-y-3 focus-within:ring-1 focus-within:ring-primaryColor/40
"
    >
      {/* TOP ROW */}
      <div className="flex flex-wrap gap-4 items-end px-4 py-3">
        {/* Report Type */}
        {showReportType && (
          <div className="flex flex-col text-sm w-40">
            <label className="text-xs text-gray-500 mb-2">Report Type</label>
            <select
              value={filters.reportType}
              onChange={(e) =>
                setFilters((p) => ({ ...p, reportType: e.target.value }))
              }
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#f0653a]
"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        )}

        {/* Date Range */}
        {showDateRange && (
          <>
            <div className="flex flex-col text-sm w-36">
              <label className="text-xs text-gray-500 mb-2">From</label>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, fromDate: e.target.value }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1   focus:ring-[#f0653a]
"
              />
            </div>

            <div className="flex flex-col text-sm w-36">
              <label className="text-xs text-gray-500 mb-2">To</label>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, toDate: e.target.value }))
                }
                className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1   focus:ring-[#f0653a]
"
              />
            </div>
          </>
        )}

        {/* Payment Mode */}
        {showPaymentMode && (
          <div className="flex flex-col text-sm w-40">
            <label className="text-xs text-gray-500 mb-2">Payment Mode</label>
            <select
              value={filters.paymentMode}
              onChange={(e) =>
                setFilters((p) => ({ ...p, paymentMode: e.target.value }))
              }
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1   focus:ring-[#f0653a]
"
            >
              <option value="">All</option>
              <option value="CASH">Cash</option>
              <option value="UPI_QR">UPI</option>
            </select>
          </div>
        )}

        {/* Staff */}
        {showStaff && (
          <div className="flex flex-col text-sm w-40">
            <label className="text-xs text-gray-500 mb-2">Staff</label>
            <select
              value={filters.staff}
              onChange={(e) =>
                setFilters((p) => ({ ...p, staff: e.target.value }))
              }
              className="border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1   focus:ring-[#f0653a]
"
            >
              <option value="">All Staff</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ACTION BUTTON */}
        <button
          onClick={hasUnappliedChanges ? onApply : onClearAll}
          disabled={!hasAppliedFilters && !hasUnappliedChanges}
          className={`ml-auto w-36 px-5 py-2 text-sm font-semibold rounded-md transition
    ${
      hasUnappliedChanges
        ? "bg-[#f0653a] text-white hover:bg-[#e05530]"
        : "border border-red-400 text-red-500 hover:bg-red-50"
    }
    disabled:opacity-40 disabled:cursor-not-allowed
  `}
        >
          {hasUnappliedChanges ? "Apply Filters" : "Clear All"}
        </button>
      </div>

      {/* CHIPS + EXPORT */}
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-[#F7BFAD]">
        {hasAppliedFilters ? (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                onRemove={() => onRemoveFilter(f.key)}
              />
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-500 italic">
            No filters applied — select filters to enable export
          </span>
        )}

        <div className="flex gap-3 ml-auto">
          <button
            disabled={!hasAppliedFilters}
            onClick={onExportPDF}
            className="flex items-center w-36 gap-2 px-4 py-2 text-sm font-semibold rounded-md border text-[#f0653a] border-[#f0653a] disabled:opacity-40"
          >
            <FileText className="size-4" />
            Export PDF
          </button>

          <button
            disabled={!hasAppliedFilters}
            onClick={onExportExcel}
            className="flex items-center w-36 gap-2 px-4 py-2 text-sm font-semibold rounded-md border text-[#2E7D32] border-[#2E7D32] disabled:opacity-40"
          >
            <PiMicrosoftExcelLogoLight className="size-4" />
            Export Excel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportFilters;
